
import { AdminState } from '@/types/adminTypes';
import { MAX_ADMINS_TO_KEEP } from './adminStateConfig';
import { updateAdminState } from './adminStateService';
import { supabase } from '@/lib/supabase';
import { clearStateCache, getCachedStateOrDefault } from './adminStateCache';

/**
 * Add an admin to the active list
 */
export const addActiveAdmin = async (adminName: string): Promise<AdminState> => {
  try {
    const currentState = getCachedStateOrDefault();
    
    // Add admin to active list if not already present
    if (!currentState.activeAdmins.includes(adminName)) {
      const updatedActiveAdmins = [...currentState.activeAdmins, adminName];
      
      return updateAdminState({
        activeAdmins: updatedActiveAdmins,
        lastActivity: new Date().toISOString()
      });
    }
    
    return currentState;
  } catch (error) {
    console.error('Error adding active admin:', error);
    return getCachedStateOrDefault();
  }
};

/**
 * Remove an admin from the active list with immediate database update
 */
export const removeActiveAdmin = async (adminName: string): Promise<AdminState> => {
  try {
    const currentState = getCachedStateOrDefault();
    
    // Remove admin from active list
    const updatedActiveAdmins = currentState.activeAdmins.filter(name => name !== adminName);
    
    // Update state with new admin list
    const updatedState = await updateAdminState({
      activeAdmins: updatedActiveAdmins,
      lastActivity: new Date().toISOString()
    });
    
    // Invalidate cache to ensure fresh state
    clearStateCache();
    
    // Force immediate database update
    await supabase
      .from('admin_state')
      .update({ 
        active_admins: updatedActiveAdmins,
        last_activity: new Date().toISOString()
      })
      .eq('id', 1);
    
    return updatedState;
  } catch (error) {
    console.error('Error removing active admin:', error);
    
    // Try one more time with a simpler approach
    try {
      const { data } = await supabase
        .from('admin_state')
        .select('*')
        .limit(1)
        .single();
      
      if (data && data.active_admins) {
        const updatedActiveAdmins = data.active_admins.filter((name: string) => name !== adminName);
        
        await supabase
          .from('admin_state')
          .update({ active_admins: updatedActiveAdmins })
          .eq('id', 1);
        
        const updatedState = {
          lastActivity: data.last_activity,
          activeAdmins: updatedActiveAdmins,
          lastSync: data.last_sync
        };
        
        return updatedState;
      }
    } catch (retryError) {
      console.error('Error in retry of removeActiveAdmin:', retryError);
    }
    
    // Return current state on error
    return getCachedStateOrDefault();
  }
};

/**
 * Ensure the active admins list doesn't exceed maximum size
 */
export const pruneActiveAdmins = (admins: string[]): string[] => {
  return admins.slice(0, MAX_ADMINS_TO_KEEP);
};
